import React, { useMemo, useState } from 'react';
import { Car } from 'lucide-react';
import { useConsultancyBundle } from '../../lib/consultancyStore';
import { inRange } from '../../lib/dateFilter';
import { useDashboardDetailDates } from '../../lib/useDashboardDetailDates';
import type { ExportRow } from '../../lib/exportReports';
import { DashboardDetailShell, DashboardMetricCard, type DashboardDetailDateProps } from './DashboardDetailShell';
import { VehicleDetailModal } from '../consultancy/VehicleDetailModal';
import { mediaSrc } from '../../lib/mediaUrl';

interface AvailableVehiclesProps extends DashboardDetailDateProps {
  onClose: () => void;
}

export function AvailableVehicles({ onClose, initialDateRange }: AvailableVehiclesProps) {
  const [bundle] = useConsultancyBundle();
  const dr = useDashboardDetailDates(initialDateRange);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);

  const vehicles = useMemo(
    () =>
      bundle.vehicles.filter(
        (v) =>
          v.status === 'available' &&
          (v.boughtAt ? inRange(v.boughtAt, dr.range) : true)
      ),
    [bundle.vehicles, dr.range]
  );

  const stockValue = useMemo(() => vehicles.reduce((s, v) => s + (Number(v.price) || 0), 0), [vehicles]);

  const selectedVehicle = useMemo(
    () => (selectedVehicleId ? bundle.vehicles.find((v) => v.id === selectedVehicleId) ?? null : null),
    [selectedVehicleId, bundle.vehicles]
  );

  const exportRows: ExportRow[] = useMemo(
    () =>
      vehicles.map((v) => ({
        Vehicle: `${v.name} ${v.model}`.trim(),
        Year: v.year ?? '—',
        Price: v.price,
        Kilometers: v.kms ?? '—',
        Location: v.location ?? '—',
        Registration: v.vehicleNumber ?? '—',
        Acquired: v.boughtAt ? new Date(v.boughtAt).toLocaleDateString() : '—',
        Status: 'Available',
      })),
    [vehicles]
  );

  return (
    <DashboardDetailShell
      onClose={onClose}
      eyebrow="Consultancy desk"
      title="Available vehicles"
      subtitle={`${vehicles.length} in stock · ₹${stockValue.toLocaleString('en-IN')} book value`}
      icon={Car}
      exportTitle="Available Vehicles Report"
      exportFilename="available-vehicles.xls"
      exportRows={exportRows}
      dateRange={dr.range}
      datePreset={dr.preset}
      onDateStart={dr.setStart}
      onDateEnd={dr.setEnd}
      onDatePreset={dr.setPreset}
      onDateClear={dr.clear}
    >
      <div className="grid grid-cols-2 gap-3 sm:max-w-lg">
        <DashboardMetricCard label="In stock" value={vehicles.length} accent />
        <DashboardMetricCard label="Book value" value={`₹${stockValue.toLocaleString('en-IN')}`} />
      </div>

      {vehicles.length === 0 ? (
        <div className="app-card shadow-sm ring-1 ring-zinc-950/[0.03]">
          <div className="app-card-inner flex flex-col items-center gap-2 py-12 text-center">
            <Car className="h-8 w-8 text-zinc-300" aria-hidden />
            <p className="text-sm text-zinc-500">No available vehicles in this date range.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {vehicles.map((vehicle) => (
            <button
              key={vehicle.id}
              type="button"
              onClick={() => setSelectedVehicleId(vehicle.id)}
              className="app-card overflow-hidden text-left shadow-sm ring-1 ring-zinc-950/[0.03] transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
            >
              {vehicle.imageUrl ? (
                <img src={mediaSrc(vehicle.imageUrl)} alt={`${vehicle.name} ${vehicle.year}`} className="h-44 w-full object-cover" />
              ) : (
                <div className="flex h-44 w-full items-center justify-center bg-zinc-50 text-zinc-300">
                  <Car className="h-10 w-10" aria-hidden />
                </div>
              )}
              <div className="app-card-inner">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="min-w-0 truncate text-base font-semibold text-zinc-900">
                    {vehicle.name} {vehicle.model}
                  </h3>
                  <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-900">
                    Available
                  </span>
                </div>
                <p className="mt-1 text-lg font-bold tabular-nums text-zinc-900">
                  ₹{vehicle.price.toLocaleString('en-IN')}
                </p>
                <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-zinc-100 pt-3 text-xs">
                  <div>
                    <dt className="text-zinc-400">Year</dt>
                    <dd className="mt-0.5 font-medium text-zinc-800">{vehicle.year || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-zinc-400">KM</dt>
                    <dd className="mt-0.5 font-medium tabular-nums text-zinc-800">
                      {vehicle.kms != null ? vehicle.kms.toLocaleString('en-IN') : '—'}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-zinc-400">Location</dt>
                    <dd className="mt-0.5 truncate font-medium text-zinc-800">{vehicle.location || '—'}</dd>
                  </div>
                </dl>
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedVehicle ? (
        <VehicleDetailModal vehicle={selectedVehicle} onClose={() => setSelectedVehicleId(null)} />
      ) : null}
    </DashboardDetailShell>
  );
}
